import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Typography } from "@mui/material";
import { DataRow } from "@outpave/ds";
import { titleCase } from "title-case";
import { Inline, Stack } from "components/layout/reusableLayouts";
import UserStatus from "../../../enums/UserStatus";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import Role from "enums/Role";
import CardStatusChip from "components/common/cards/CardStatusChip";
import CardDetailsDrawer from "components/common/cards/CardDetailsDrawer";
import type { CardRowData } from "utils/cards/cardRowData";
import { transactionMeta } from "utils/transactions/transactionRow";

interface AssignedCardsProps {
  cards: CardRowData[];
  handleCreateNewCard: () => void;
  userStatus: string;
  /** Refetches the list after the details drawer changed a card. */
  onCardsChanged: () => void;
}

/**
 * The user's cards, drawn as the same rows as their transactions. A row opens
 * the card's details over this drawer.
 */
const AssignedCards: React.FC<AssignedCardsProps> = (props) => {
  const { cards, handleCreateNewCard, userStatus, onCardsChanged } = props;
  const isAdminOrHigher = useCheckUserRole(Role.Admin);
  const { t } = useTranslation();
  const [openCardId, setOpenCardId] = useState<string | null>(null);

  return (
    <Stack gap="var(--space-4)">
      {cards.length === 0 ? (
        <Typography
          variant="body1"
          sx={{ color: "var(--color-text-tertiary)" }}
        >
          {t("No cards found")}
        </Typography>
      ) : (
        <Stack gap="0">
          {cards.map((card) => (
            <DataRow
              key={card.id}
              type="amount"
              title={card.cardNumber}
              // Status on the second line, as on the transaction rows, so a nickname keeps the first.
              subtitle={
                <Inline gap="var(--space-3)" wrap={false}>
                  {transactionMeta([
                    t(titleCase(card.type.toLowerCase())),
                    card.cancelation
                      ? t("CancelsOn", { date: card.cancelation })
                      : card.expiration
                        ? t("ExpiresOn", { date: card.expiration })
                        : undefined,
                  ])}
                  <CardStatusChip
                    status={card.cardStatus}
                    pendingTerms={card.pendingTerms}
                  />
                </Inline>
              }
              onClick={() => setOpenCardId(card.id)}
            />
          ))}
        </Stack>
      )}
      {(userStatus === UserStatus.ACTIVE ||
        userStatus === UserStatus.PENDING) &&
        isAdminOrHigher && (
          <Inline justify="end">
            <Button
              variant="default-outlined"
              onClick={handleCreateNewCard}
              size="small"
            >
              {t("Add Card")}
            </Button>
          </Inline>
        )}

      {openCardId && (
        <CardDetailsDrawer
          open
          cardId={openCardId}
          onClose={(changed) => {
            setOpenCardId(null);
            if (changed) onCardsChanged();
          }}
        />
      )}
    </Stack>
  );
};

export default AssignedCards;
